import {moment} from 'external/moment'
import { toArray } from 'external/lodash'

import { React, useHistory } from 'external/react'

import {
  clsx,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  makeStyles
}
from 'external/material'

import {parentDomain, contentHost, apiProtocol, contentLink} from 'helpers/url'

import {useImmutableForm} from 'helpers/form-hook'
import useValidations from 'helpers/validations-hook'
import {createPost} from 'helpers/command'
import {uuid} from 'helpers/utils'

import DatePicker from '../date-picker'
import CheckBoxList from '../checkboxlist'

import TextInput from './TextInput'
import Select from './Select'
import validations from './validations'

import UploadFiles from 'uploads/UploadFiles'
import uploadsStore from 'uploads/redux/store'

const initialState = {
  attempted: false,  
  title: '',
  group: '',
  startDate: new Date(),
  endDate: moment().endOf('day').toDate(),
  instructions: '',
  students: [],
  includeInRollbook: false,
  score: '0',
  files: [],
  folderId: uuid(),
  disableEditGroups: false
}

const useStyles = makeStyles(createStyles)
const useStudentListStyles = makeStyles(studentListStyles)

export default function CreateHomeworkForm ({context}) {

  const classes = useStyles()
  const studentListClasses = useStudentListStyles()
  const history = useHistory()  
  const theme = useTheme()

  //const notificationsContext = useContext(NotificationsContext)

  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  
  const {translate, homeworkContext, link, linkQuery, groups, profile: {Passport: passport}} = context
  const {open, editing, editState} = homeworkContext.createHomework
  const {state, setState, handleChange, resetState, createHandler, updateState: updateFormState, hasStateChanged} = useImmutableForm(initialState)
  const {errors, errorCount, validate, resetErrors} = useValidations(validations)

  const {students: myStudents = []} = groups.find(g => g.id == state.group) || {}


  function closeDialog () {    
    context.updateState({homeworkContext: {createHomework: {open: {$set: false}, editing: {$set: false}, editState: {$set: undefined}}}})
    resetState({folderId: false})
    resetErrors()
    uploadsStore.dispatch(resetUploads())
  }

  const resetUploads = () => (dispatch) => {      
    dispatch({
      type: 'RESET_UPLOADS'
    })
  }

  function loadUploads() {
    if(!editState.properties.files) return

    editState.properties.files.map(f=> {
      f.server = `${apiProtocol}://${contentHost}.${parentDomain()}/files`;
    });

    const loadUploads = () => (dispatch) => {      
      dispatch({
        type: 'LOAD_UPLOADS',
        payload: editState.properties.files
      })
    }
    uploadsStore.dispatch(loadUploads())
  }

  async function save() {

    if(!hasStateChanged()) return;

    let files = toArray(uploadsStore.getState().UploadFile.fileProgress)
        .filter(f => !f.deleted && f.progress == 100)
        .map(file => { return {
            name: file.file.name,
            folder: file.folder
          }
        }) 
     
    if (validate({...state, attempted: true})) {
      const {title,group,startDate,endDate,instructions,students,score, includeInRollbook} = state;      
      const homework = {
          title, 
          group, 
          startDate: moment(startDate).toISOString(), 
          endDate: moment(endDate).toISOString(), 
          students,
          properties: { instructions, score, files, includeInRollbook}
        }      
      
      const saveCommand = createPost('homework', 'saveHomework')({id: editing ? editState.id : undefined, ...homework})
      const [homeworkId] = await saveCommand
      if (homeworkId) {
        //createHomeworkNotification(context, {group, id: homeworkId, students})
        await context.updateState({homeworkContext: {homeworks: {refreshes: {$apply: i => i+1 }}}})
        closeDialog()
      }
      else {
        setState({...state, attempted: true})
      }
    }
    else {
      setState({...state, attempted: true})
    }
  }

  React.useEffect(() => {

    if (!editing && state.group != context.query.group)
      setState({...state, group: context.query.group})
     
  }, [context.query.group])

  React.useEffect(() => { validate(state) }, [state])


  React.useEffect(() => {
    if (!editing && open) {        
      setState({...state, folderId: uuid()})
    }       
  }, [open])

  React.useEffect(() => {
    if(editState) {       
      const {title, group, startDate, endDate, properties} = editState
      const includeInRollbook = properties.includeInRollbook || false
      const students = editState.students.map(s=>s.student)
      const disableEditGroups = editState.students.some(s=> s.properties.files && s.properties.files.length > 0)
      const folderId = properties.files && properties.files.length > 0?properties.files[0].folder.substring(properties.files[0].folder.indexOf('/')+1):uuid()      
      
      setState({title,group,startDate,endDate,students,disableEditGroups,folderId,...properties, includeInRollbook});
      loadUploads()
    } else {
      resetState({folderId: uuid()})
    }
  }, [editState])

  // console.log('state', state)
  // console.log('files', editState?JSON.stringify(editState.properties.files):'')
  // console.log('myStudents', myStudents)

  return (
    <Dialog
      className={classes.dialog}
      fullScreen={fullScreen}     
      maxWidth="lg" 
      open={open}
      onClose={closeDialog}      
    >           
    <DialogTitle className={classes.title}>
        <Box className={classes.titleText}>{editing ? translate('edit-homework'): translate('new-homework')}</Box>
        <Box className={classes.titleActions}>
          <Button
            variant="contained"
            className={clsx(classes.button, "cancel")}
            onClick={closeDialog}>
              {translate('cancel')}
          </Button>
          <Button
            variant="contained"
            className={clsx(classes.button, "create")}
            onClick={save}>
              {translate(editing?'save':'assign')}
          </Button>
        </Box>      
      </DialogTitle>
      <DialogContent>
        <Box display="flex">
          <Box className={clsx(classes.leftColumn)}>
            <Box className={clsx(classes.field, "title")}>
              <TextInput
                controlFullWidth
                controlVariant="outlined"
                controlMargin="dense"
                id="title"
                inputAutoComplete="title"
                labelRequired
                inputAutoFocus
                caption={translate('title')}
                value={state.title}
                inputOnChange={handleChange('title')}
                controlError={errors.title.length > 0}
                errorMessage={errors.title.length > 0 ? translate(errors.title) : undefined}
              />
            </Box>
            
            <Box className={clsx(classes.instructions, classes.field, {error: errors.instructions.length > 0})}>
              
              <Box className={clsx("description", "label")}>{translate('instructions')}:</Box>
              <textarea id='homeworkInstructions' value={state.instructions} onChange={handleChange('instructions')} />
              <label htmlFor='homeworkInstructions'>{translate(errors.instructions)}</label>
            </Box>
            <Box className={clsx(classes.scoreRollbook, "score-rollbook")}>
              <Box className={clsx(classes.field, "score")}>
                  <TextInput
                    controlFullWidth
                    controlVariant="outlined"
                    controlMargin="dense"
                    id="score"
                    inputAutoComplete="score"
                    labelRequired
                    inputAutoFocus
                    caption={translate('score')}
                    value={state.score}
                    inputType='number'
                    inputInputProps={{min:0, max: 1000}}
                    inputOnChange={handleChange('score')}             
                  />
              </Box>
              <Box className={clsx(classes.field, 'rollbook')}>
               <FormControlLabel
                 control={<Checkbox color="primary" checked={state.includeInRollbook} onChange={handleChange('includeInRollbook', e => e.target.checked)} />}
                 label={`${translate('include-in-rollbook')}:`}
                 labelPlacement="start"
                />
            </Box>
            </Box>
            <Box className={clsx(classes.dates)}>
              <Box className={clsx(classes.dates, 'dateField')}>
                <Box className={clsx(classes.dates, "labelName")}>{translate('homework-from-date')}:</Box>
                <DatePicker 
                  date={moment(state.startDate).toDate()}
                  minDate={new Date()}
                  maxDate={moment().add(1,'year').toDate()}
                  context={context}
                  onChange= {createHandler((startDate)=> { const endDate = moment(state.endDate).isAfter(startDate)
                    ? state.endDate
                    : moment(startDate).endOf('day').toDate()
                    return {startDate, endDate}
                  })} 
                />
              </Box>

              <Box className={clsx(classes.dates, 'dateField')}>
                <Box className={clsx(classes.dates, "labelName")}>{translate('homework-to-date')}:</Box>
                <DatePicker 
                  date={moment(state.endDate).toDate()}
                  minDate={moment(state.startDate).toDate()}
                  maxDate={moment().add(1,'year').toDate()}
                  context={context}
                  onChange= {createHandler((endDate) => ({endDate}))}
                />
              </Box>
            </Box>
            <Box className={clsx(classes.resources)}>
              <Box className={clsx(classes.resources, 'label')}>{translate('homework-material')}</Box>
              <Box className={clsx(classes.resources, 'container')}>
              <UploadFiles 
                server={`${apiProtocol}://${contentHost}.${parentDomain()}/files`}
                folder={`homeworks/${state.folderId}`} 
                translate={translate} 
                buttonText={translate('agregar-recurso')}
                readOnly={false}
                downloadServer={contentLink(passport.id)}
                />
              </Box>
            </Box>
          </Box>
          <Box className={clsx("rightColumn")}>
            <Box>
              <Box className={clsx(classes.field, "group")}>
                <Select
                  controlFullWidth
                  controlVariant="outlined"
                  controlMargin="dense"
                  selectValue={state.group}
                  selectOnChange={createHandler((event)=> ({ group: event.target.value, students: [] }) )}
                  caption={translate('groups_label')}
                  controlError={errors.group.length > 0}
                  errorMessage={errors.group.length > 0 ? translate(errors.group): undefined}
                  mapValue={o => o.id}
                  mapText={o => o.name}
                  options={groups}
                  selectDisabled={state.disableEditGroups}
                  />
              </Box>          
              <Box className={clsx(studentListClasses.wrapper, {error: errors.students.length > 0})}>
                  <Box className={clsx("students")}>{translate('students')}:</Box>
                  <CheckBoxList 
                    classes={studentListClasses}
                    canSelectAll
                    selectAllLabel={translate('select-all')}
                    handleSelectAll={students => updateFormState({students: {$set: students}})}
                    options={myStudents} 
                    checked={state.students} 
                    mapLabel={o => o.name} 
                    mapValue={o => o.id}
                    // disabled={state.disableEditGroups}
                    handleToggle={student => {
                      const index = state.students.indexOf(student)
                      updateFormState(
                        index < 0
                        ? {students: {$push:[student]}}
                        : {students: {$splice:[[index, 1]]}}
                      )
                    }}
                  />
                  <Box>
                    <label>{translate(errors.students)}</label>
                  </Box>
              </Box>
            </Box>    
          </Box>  
        </Box>  
      </DialogContent>      
    </Dialog>
  )
}


function createStyles(theme) {

  return {
    root: { 
      display: 'flex'
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',      
      flexGrow:1,
      marginRight: 30
    },
    rightColumn: {
      display: 'flex',
      flexGrow:1,
      flexDirection: 'column',      
    },
    titleText: {
      textTransform: 'capitalize',
      fontWeight: 'bold',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',  
      position: 'absolute',
      color: '#4e576e',
      marginTop: 10    
    },
    titleActions: {
      float: 'right'
    },
    title: {
      borderBottom: '1px solid #ccc',       
      clear: 'both',
      marginBottom: 35
    },
    
    instructions: { 
      minHeight: '100px',
      marginBottom: 35,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      '& .description': {
        fontSize: '12pt',
        fontWeight: 'bold',
        marginBottom: 5,
        color: '#878ea1',
        display: 'block',          
        whiteSpace: 'nowrap',
        textTransform: 'capitalize'
      },
      '& > textarea': {
        resize: 'none',
        flexGrow: 1,
        borderRadius: '4px',
        border: 'solid 1px #888',        
        fontSize: '16px',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        width: '100%',
        minHeight: 100,
        padding: 10
      },
      '& > label': {
        visibility: 'hidden',
        color: '#f44336',
        fontSize: '12px'
      },
      '&.error': { 
        '& > textarea': {
          border: 'solid 1px #f44336'
        },
        '& > label': {
          visibility: 'visible'          
        }
      }
    },
    dates: {     
      display: 'flex', 
      '& .dateField': {        
        flexDirection: 'column',      
        flexGrow:1,
        marginBottom:  35,        
        '&:first-of-type': {
          marginRight: 10,
        },
        '&:last-of-type': {
          marginLeft: 10,
        },
        fontSize: '14px',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        '& .labelName': {
          marginBottom: 5,
          fontSize: '12pt',
          fontWeight: 'bold',
          color: '#878ea1',
          display: 'block',          
          whiteSpace: 'nowrap',
          textTransform: 'capitalize'
        },
        '& .date-input': {
          border: '1px solid #999',
          padding: '14px 10px',
          borderRadius: 4,
        },
        '& svg': {
          marginRight: 10
        }
      }      
    },
    scoreRollbook: {
      display: 'flex'
    },
    resources: {
      '& .container': {
      },
      "& .label": {
        fontSize: '12pt',
        fontWeight: 'bold',
        color: '#878ea1',
        display: 'block',          
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
        margin: '5px 0'
      },
    },
    field: {
      marginBottom: 35,
      minWidth: 500,      
      '&.group': {
        minWidth: 300,      
        paddingRight: 200
      },
      '&.score': {
        minWidth: 150,      
        '& div': {
          maxWidth: 150
        }
      },
      '&.rollbook': {
        marginLeft: 20,
        marginTop: 12,
        minWidth: 200,      
        '& div': {
          maxWidth: 150
        }
      },
      "& label": {
        marginLeft: 0,
        whiteSpace: 'nowrap',
        "& .MuiFormControlLabel-label": {
          color: '#878ea1',
          fontSize: '12pt',
          fontWeight: 'bold',
        }
      },
      "& > div": {
        "& .label": {
          marginBottom: 5,
          fontSize: '12pt',
          fontWeight: 'bold',
          color: '#878ea1',
          display: 'block',          
          whiteSpace: 'nowrap',
          textTransform: 'capitalize'
        },
        "& .input": {
          flexGrow: 1,
          "& input": {
            borderRadius: 4,
            border: 'solid 1px #aab7bf !important',
          },
          "& .select": {
            "& .MuiInput-underline:before": {
              content: 'none',
              border: 'none'
            },
            "& .MuiSelect-select": {
              border: 'solid 1px #aab7bf',
              borderRadius: 4,
              padding: 10,
              paddingRight: 24
            },
          }
        }
      }
    },
    button: {
      width: 120,
      fontSize: "13pt",
      textTransform: "none",
      boxShadow: 'none',
      borderRadius: 4,
      "&:first-child": {
        marginRight: 10
      },
      "&:last-child": {
        marginLeft: 10
      },
      "&.cancel": {
        border: "solid 1px #3d4556",
        backgroundColor: '#fff',
        color: "#3d4556",
        '&:hover': {
          color: "#000",
          border: "solid 1px #000",
          backgroundColor: '#eaf5ff',
        },
      },
      "&.create" : {
        color: "#fff",
        border: 'solid 1px #09b2b2',
        backgroundColor: '#2c75bd',
        color: '#fff',
        textTransform: 'capitalize',
        '&:hover': {
          backgroundColor: '#29739f',
          borderColor: '#29739f',
          boxShadow: 'none',
        },
      }
    },
    actions: {
      padding: '8px 24px 30px 24px',
    }
  }
}



function studentListStyles(theme) {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    wrapper: {
      '& label': {
        visibility: 'hidden',
        color: '#f44336',
        fontSize: '12px'
      },
      '&.error': {       
        '& label': {
          visibility: 'visible'          
        }
      },
      "& .students": {
        marginBottom: 5,
        fontSize: '12pt',
        fontWeight: 'bold',
        color: '#878ea1',
        display: 'block',          
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
      },
      '& > ul': {
        border: '1px solid #888',
        borderRadius: 3,
        minHeight: 500
      }
    }
  }
}
